import { ref, watch, computed } from 'vue'
import shopify from '@/assets/illus/shopify_logo.svg'
import woocommerce from '@/assets/illus/woo_logo.png'
import { fetchData } from '@/utils/fetch'
import { useUserStore } from '@/pinia/user'
import { useShipStore } from '@/pinia/ship'
import { createSharedComposable } from '@vueuse/core'
import { openURLWithSSO } from '@/utils/utils'
import { usePlugin } from '@/hooks/usePlugin'
import { openContactApp } from '@/utils/utils'
import { retrieveDropshippingUrl } from '@/api/dropshipping'
import { isMobile } from '@/utils/constants'

export const useDropshipping = createSharedComposable(() => {
  const { $toast, $confirm } = usePlugin()

  const userStore = useUserStore()
  const shipStore = useShipStore()

  const dialogVisible = ref(false)
  const options = ref([shopify, woocommerce])
  const activeIndex = ref(0)

  const showWooView = ref(false)
  const wooUrl = ref('')

  const focus = ref(false)

  const isGoDropshipping = ref(false)

  const countryCode = computed(() => shipStore.country.countryCode)

  // 是否绑定店铺
  const hasBoundStore = computed(() => {
    const userInfo = userStore.userInfo
    const storeList = userInfo.storeList || []
    const filterList = storeList.filter(item => ['shopify', 'woocommerce'].includes(item.channel))
    return filterList.length > 0
  })

  watch(dialogVisible, (value) => {
    if (!value) {
      activeIndex.value = 0
      showWooView.value = false
      wooUrl.value = ''
    }
  })

  const onCancel = () => {
    if (showWooView.value) {
      showWooView.value = false
    } else {
      dialogVisible.value = false
    }
  }

  const onConfirm = async () => {
    if (activeIndex.value === 0) { // shopify
      openURLWithSSO('https://apps.shopify.com/bestdealer')
    } else { // woocommerce
      showWooView.value = true
    }
  }

  const onGoToLink = async () => {
    if (!wooUrl.value) return

    const { code, data } = await fetchData('/dsp-app/store/getStoreAuthorizeLinkUrl?channel=WOOCOMMERCE&storeDomain=' + wooUrl.value)
    if (code === 200) {
      setTimeout(() => {
        openURLWithSSO(data)
      }, 100)
    } else {
      const messages = {
        1401: 'Please contact your agent to enable multi-store binding.'
      }
      const error = messages[code] || 'The store domain is invalid'
      $toast(error, { type: 'error' })
      openContactApp()
    }
  }

  const isDropshipping = ref(false)
  const dropshippingUrl = ref()

  const onDropshipping = async (offerId) => {
    // 有绑定店铺跳转到app
    if (hasBoundStore.value) {
      if (isDropshipping.value) return
      try {
        isDropshipping.value = true
        dropshippingUrl.value = await retrieveDropshippingUrl({
          countryCode: countryCode.value,
          offerId
        })

        skipToApp()
      } catch (error) {
        if (!isMobile && error.code === 5216) {
          $confirm('Warning', 'You have reached your sourcing request limit. Please wait until a product is "Sourcing Success" before continuing', {
            confirmText: 'Go Sourcing Requests'
          }).then(() => {
            openURLWithSSO(error.data)
          })
        } else {
          $toast(error.message, { type: 'error' })
        }
      } finally {
        isDropshipping.value = false
      }
    } else {
      isGoDropshipping.value = false
      dialogVisible.value = true
    }
  }

  const skipToApp = async () => {
    if (!isMobile) {
      await openURLWithSSO(dropshippingUrl.value)
      dialogVisible.value = false
    } else {
      isGoDropshipping.value = true
      dialogVisible.value = true
    }
  }

  const onDropshippingOK = () => {
    isGoDropshipping.value = false
    dialogVisible.value = false
  }

  return {
    dialogVisible,
    options,
    activeIndex,
    showWooView,
    wooUrl,
    focus,
    isGoDropshipping,
    hasBoundStore,
    onCancel,
    onConfirm,
    onGoToLink,
    onDropshipping,
    onDropshippingOK
  }
})
